import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Inline keyboard hint pill. Used in tooltips, command palette items, dropdown
 * menu items, and the shortcuts help panel. Cross-platform symbols come from
 * the SHORTCUTS registry — this component is purely visual.
 */
const Kbd = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & { "data-slot"?: string }
>(({ className, children, ...props }, ref) => (
  <kbd
    ref={ref}
    data-slot="kbd"
    className={cn(
      "inline-flex h-5 min-w-[1.25rem] select-none items-center justify-center rounded border border-border/60 bg-muted px-1 font-mono text-[10px] font-medium leading-none tracking-tight text-muted-foreground",
      className,
    )}
    {...props}
  >
    {children}
  </kbd>
));
Kbd.displayName = "Kbd";

/**
 * Render a multi-key sequence as separate `<Kbd>` pills with a "then" hint.
 * Splits on whitespace and the literal "then" word.
 */
function KbdSequence({
  sequence,
  className,
}: {
  sequence: string;
  className?: string;
}) {
  const tokens = sequence
    .split(/\s+then\s+|\s+/i)
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length <= 1) {
    return <Kbd className={className}>{sequence}</Kbd>;
  }
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      {tokens.map((token, i) => (
        <React.Fragment key={`${token}-${i}`}>
          {i > 0 && (
            <span className="text-[10px] text-muted-foreground/60">then</span>
          )}
          <Kbd>{token}</Kbd>
        </React.Fragment>
      ))}
    </span>
  );
}

export { Kbd, KbdSequence };
